import React, { useState, useEffect } from 'react';
import 'components/select-or-create-item/select-or-create-item.scss';
import { Button, Form, FormItemProps, Select } from 'antd';
import { Divider, Typography } from 'antd';
import { LabelValue } from 'components/view-models';
import { fixSelectOnScroll } from 'tools/view-hooks/fix-autocomplete';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

export interface SelectOrCreateItemProps extends FormItemProps {
    selectPlaceholder?: string;
    selectOptions: LabelValue[];
    selectMode?: 'multiple' | 'tags';
    createFormItemName: string;
    optionFilterProp: string;
    createComponent: React.ReactNode;
    createTitle: string;
    labelItem?: string;
    requiredForm: boolean;
    onClickSelect?: () => void;
    onBlurSelect?: () => void;
    isOpen?: boolean;
    rulesComponent?: any;
    defaultCreateVisible?: boolean;
}

const SelectOrCreateItem: React.FC<SelectOrCreateItemProps> = (props) => {
    const [isCreateVisible, setIsCreateVisible] = useState(props.defaultCreateVisible ?? false);
    const [allowSearch, setAllowSearch] = useState(false);
    const [createCount, setCreateCount] = useState(0);

    // Sync visibility when defaultCreateVisible changes (e.g., after extracted data arrives)
    useEffect(() => {
        if (props.defaultCreateVisible) {
            setIsCreateVisible(true);
            setCreateCount(1);
        }
    }, [props.defaultCreateVisible]);

    const onSelectChange = (value: number | number[]) => {
        if (Array.isArray(value)) {
            const newCreateCount = value.filter((v) => v === -1).length;
            setCreateCount(newCreateCount);
            setIsCreateVisible(newCreateCount > 0);
            return;
        }
        setIsCreateVisible(value == -1);
        setCreateCount(value == -1 ? 1 : 0);
    };

    useEffect(() => {
        if (props.selectOptions.length > 3) {
            setAllowSearch(true);
        }
    });

    const renderFormListContent = (fields: any[], actions: any, errors: any) => {
        // A hydrated Form.List already owns its fields. Only add missing manual-create rows;
        // removing here would discard values before defaultCreateVisible's effect can run.
        if (props.selectMode === 'multiple' && createCount > 0) {
            const currentFieldCount = fields.length;
            if (currentFieldCount < createCount) {
                for (let i = currentFieldCount; i < createCount; i++) {
                    actions.add();
                }
            }
        }

        return (
            <div>
                <Typography.Title level={5}>{props.createTitle}</Typography.Title>
                {props.selectMode === 'multiple' ? (
                    <>
                        {fields.map((field, index) => (
                            <div key={field.key} style={{ marginBottom: '16px', position: 'relative' }}>
                                {index > 0 && (
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => actions.remove(index)}
                                        style={{ position: 'absolute', right: 0, top: 0 }}
                                    />
                                )}
                                {React.cloneElement(props.createComponent as React.ReactElement, {
                                    fieldKey: field.fieldKey,
                                    name: field.name,
                                })}
                            </div>
                        ))}
                        <Button type="dashed" onClick={() => actions.add()} block icon={<PlusOutlined />}>
                            Agregar nuevo
                        </Button>
                    </>
                ) : (
                    props.createComponent
                )}
                {errors ? <Typography.Text style={{ color: 'red' }}>{errors.toString()}</Typography.Text> : null}
            </div>
        );
    };

    return (
        <div className={'select-or-create-item'}>
            <Form.Item label={props.label} required={props.requiredForm} {...props}>
                <Select
                    allowClear
                    showSearch={allowSearch}
                    placeholder={props.selectPlaceholder}
                    options={[{ label: 'Crear Nuevo', value: -1 }, ...props.selectOptions]}
                    optionFilterProp={props.optionFilterProp}
                    onChange={onSelectChange}
                    mode={props.selectMode}
                    open={props.isOpen}
                    onClick={props.onClickSelect}
                    onBlur={props.onBlurSelect}
                    getPopupContainer={fixSelectOnScroll}
                />
            </Form.Item>
            {isCreateVisible && (
                <div>
                    <Divider />
                    <Form.List name={props.createFormItemName} rules={props.rulesComponent}>
                        {(fields, actions, { errors }) => renderFormListContent(fields, actions, errors)}
                    </Form.List>
                </div>
            )}
        </div>
    );
};

export default SelectOrCreateItem;
